import React, { useState } from "react";
import "./ReviewSiteButton.scss";
import { recordSource } from "../../api/inviteApi";
import { useTranslation } from "react-i18next";
import isFunction from "lodash/isFunction";
import { datadogRum } from "@datadog/browser-rum";
import BeatLoader from "react-spinners/BeatLoader";
import c from "classnames";

const isSiteGooglePlay = (target) =>
  typeof target === 'string' && target?.includes("play.google.com");
const isSiteAppleAppStore = (target) =>
  typeof target === 'string' && target?.includes("apps.apple.com"); 

const getButtonColorClass = (target, site, customSite) => {
  if (isSiteAppleAppStore(target)) {
    return `APPLE`;
  } else if (isSiteGooglePlay(target)) {
    return `GOOGLE`;
  } else if (!customSite) {
    return `${site}`;
  }
};

const getButtonBackgroundImage = (target, site, logoUrl, customSite) => {
  if (logoUrl) {
    return logoUrl;
  } else if (isSiteAppleAppStore(target)) {
    return `/img/logos/primary/APPLE.png`;
  } else if (isSiteGooglePlay(target)) {
    return `/img/logos/primary/GOOGLE.png`;
  } else if (!customSite) {
    return `/img/logos/primary/${site}.png`;
  } else {
    return `/img/logos/primary/CUSTOM.svg`;
  }
};

/**
 * @param {string} site source constant
 * @param {string} section primary or secondary
 * @param {integer} position position within the section
 */
export default function ReviewSiteButton({
  site,
  inviteCode,
  section = "secondary",
  position,
  target,
  logoUrl,
  customSite,
  isSurvey,
}) {
  const [navigating, setNavigating] = useState(false);
  const nav = () => {
    setNavigating(true);
    recordSource(inviteCode, site, section, position)
      .catch((error) => console.error(error))
      .then(() => {
        if (isFunction(target)) {
          // beware window.open in here
          // https://stackoverflow.com/questions/20696041/window-openurl-blank-not-working-on-imac-safari
          target();
        } else {
          datadogRum.addAction("source_select", { site, target });
          window.location = target;
        }
      });
  };
    const colorClass = getButtonColorClass(target, site, customSite);


  if (target) {
    return (
      <div
        onClick={() => nav()}
        className={c("review-site-button", section, {
          [colorClass]: !!colorClass,
        })}
      >
        {section === "primary" ? (
          <PrimaryButton
            site={site}
            target={target}
            navigating={navigating}
            customSite={customSite}
            isSurvey={isSurvey}
            logoUrl={logoUrl}
          />
        ) : (
          <SecondaryButton
            site={site}
            navigating={navigating}
            customSite={customSite}
            isSurvey={isSurvey}
            logoUrl={logoUrl}
          />
        )}
      </div>
    );
  } else {
    return null;
  }
}

const PrimaryButton = ({ site, navigating, logoUrl, target, customSite, isSurvey }) => {
  const { t } = useTranslation("", { useSuspense: false });

  const backgroundImage = getButtonBackgroundImage(target, site, logoUrl, customSite);
  const colorClass = getButtonColorClass(target, site, customSite);

  return (
    <>
      <div className="logo primary">
        <img
          data-action-title={"primary " + (customSite ? "CUSTOM" : site)}
          className="logo-img"
          src={
            backgroundImage
          }
          alt={site}
        />
      </div>
      <div
        data-action-title={"primary " + (customSite ? "CUSTOM" : site)}
        className={c("label", {
          [colorClass]: !!colorClass,
        })}
      >
        {navigating ? (
          <BeatLoader color="#ffffff" />
        ) : isSurvey ? (
          site
        ) : (
          t("reviewUsOn", { site: t(site) })
        )}
      </div>
    </>
  );
};

const SecondaryButton = ({
  site,
  navigating,
  logoUrl,
  customSite,
  isSurvey,
}) =>
  navigating ? (
    <BeatLoader />
  ) : (
    <>
      {isSurvey || customSite ? (
        <span data-action-title={"Secondary CUSTOM"}>{site}</span>
      ) : (
        <img
          data-action-title={`Secondary ${site}`}
          className="logo"
          src={logoUrl || `/img/logos/secondary/${site}.png`}
          alt={site}
        />
      )}
    </>
  );
